/**
 * Le test du matériel avant le live, en machine d'états pure : l'orchestration que portaient les
 * seize `useState` de l'ancien `PreCallCheckPanel`, sans écran.
 *
 * Deux moitiés :
 *  - l'ÉTAT (`DeviceCheckState`) : ce que les sondes ont constaté (accès au micro et à la caméra,
 *    latence, périphériques, souvenir du dernier test, image lue) et ce que l'élève a fait (commencer,
 *    passer une étape, répondre « je n'entends rien », choisir un micro…). Il n'avance que par des
 *    événements (`deviceCheckReducer`), l'heure n'y entre jamais : les délais (l'écoute du micro, la
 *    réussite qu'on laisse voir) sont des événements que le crochet pose ;
 *  - la VUE (`deriveDeviceCheck`) : ce qui s'en déduit — le visage du test (accueil, déjà vérifié,
 *    parcours), les quatre étapes et leur phrase, l'étape ouverte, son conseil et ses gestes, l'état
 *    explicite des deux accès, et le bilan que l'entrée lit (verdicts, essais, fini ou pas).
 *
 * Le réducteur rend aussi les SIGNAUX de la transition (`DeviceCheckSignal`) : les événements
 * Amplitude du précall (les mêmes noms et propriétés qu'avant), les fils d'Ariane de la machine, et
 * le refus d'un accès, qui était absorbé : le crochet les envoie, rien ne se perd entre deux rendus.
 *
 * Rien de propre à l'élève : le public (`audience`) choisit seulement qui « t'attend » dans les
 * textes, comme avant — le tuteur pourra lire la même machine.
 *
 * Les règles d'avant ne bougent pas : elles vivent dans `deviceCheckRules`, `precallActions`,
 * `troubleshootingLadders`, `deviceCheckCopy`, et cette machine les appelle. Chaque décision portée
 * ici garde le commentaire de son ancien emplacement, en court.
 */
import {
    CAUSE,
    IMAGE,
    STATUS,
    cameraStatus,
    cameraVerdict as cameraVerdictRule,
    microphoneStatus,
} from "./deviceCheckRules"
import { permissionChoiceIndex } from "./browserPermissionPaths"
import { audienceCopy, browserLabel, cameraDetail, memoryMissDetail, microphoneDetail } from "./deviceCheckCopy"
import { visibleActions as visibleActionsRule } from "./precallActions"
import { PRECALL_EVENT } from "./precallTracking"
import {
    CAMERA_LADDER,
    MICROPHONE_LADDER,
    NETWORK_LADDER,
    SPEAKER_LADDER,
    relevantRungs,
} from "./troubleshootingLadders"
import { networkDetail, networkStatus } from "./useNetworkCheck"

// ── Les formes ───────────────────────────────────────────────────────────────────────────────────

export type DeviceCheckStepId = "network" | "microphone" | "speaker" | "camera"
export type DeviceStatus = "ok" | "warn" | "bad" | "pending"
/** Pourquoi un appareil répond ou non (`CAUSE` de `deviceCheckRules`). */
export type DeviceCause =
    "not_asked" | "granted" | "denied" | "blocked_by_system" | "no_device" | "in_use" | "dark_image" | "unknown"
export type ImageReading = "looking" | "judged" | "gave_up"
export type DeviceCheckAudience = "student" | "tutor"
export type MediaDeviceKind = "audioinput" | "videoinput"
export type MediaDeviceOption = { deviceId: string; label: string }

/**
 * L'accès au micro ou à la caméra, dit en clair : à demander, accordé, refusé (par l'élève, son
 * navigateur ou son système), ou indisponible (pas d'appareil, occupé, une panne qu'on ne sait pas
 * nommer). Avant, un refus se fondait dans un voyant rouge parmi d'autres.
 */
export type DevicePermission = "prompt" | "granted" | "denied" | "unavailable"

/** Un geste du conseil : sa phrase, et son glyphe quand il en a un (un nom FontAwesome 5). */
export type DeviceGuidanceLine = { text: string; icon?: string }

/** Une étape de l'échelle de dépannage (`troubleshootingLadders`). */
type Rung = {
    id: string
    label: string
    picker?: MediaDeviceKind
    tip: (context: {
        isWeb: boolean
        cause: string
        browser: string
        audience: DeviceCheckAudience
    }) => string | { icon?: string; text: string }[]
}

type LadderState = { index: number; attempts: string[] }

export type DeviceCheckEnv = {
    isWeb: boolean
    /** La famille du navigateur (`browserFamily`), `autre` hors du web. */
    browser: string
    /**
     * Le navigateur des chemins d'accès (`permissionBrowserOf`), le téléphone séparé : sur iPhone, le
     * chemin passe par « aA », pas par le menu de Safari. `browser` reste la famille, pour les mots et
     * pour Amplitude.
     */
    permissionBrowser: string
    audience: DeviceCheckAudience
}

export type DeviceCheckState = {
    env: DeviceCheckEnv

    // Ce que les sondes ont constaté
    causes: { camera: DeviceCause; microphone: DeviceCause }
    /** Une sonde d'accès tourne : « Autoriser » attend. */
    checking: boolean
    /** La latence mesurée, en ms ; `-1` : la mesure a échoué ; `null` : pas encore mesurée. */
    latencyMs: number | null
    devices: { audioinput: MediaDeviceOption[]; videoinput: MediaDeviceOption[] }
    memory: { ready: boolean; matches: boolean; reason: string | null; changedParts: string[] }
    isVideoMuted: boolean
    isObstructed: boolean
    /** Ce que l'analyse de l'image a fini par dire, et qui ne se dédit pas. */
    imageSeen: ImageReading
    /** La caméra choisie ne s'ouvre pas : pourquoi. */
    cameraDeviceError: DeviceCause | null
    /** Une image est déjà arrivée dans l'aperçu (depuis l'ouverture de l'étape caméra). */
    previewEverOpened: boolean

    // Ce que l'élève a fait
    started: boolean
    /** « Revérifier » : le souvenir ne vaut plus pour cette entrée. */
    forceFull: boolean
    skipped: Partial<Record<DeviceCheckStepId, true>>
    speakerHeard: boolean | null
    soundPlayed: boolean
    chiming: boolean
    soundBlocked: boolean
    /** Seul le dernier appui sur « Écouter » a le droit de conclure. */
    chimeRun: number
    seesSelf: boolean | null
    /** Le fait retenu : une fois le micro entendu, il l'est pour le reste du parcours. */
    micHeard: boolean
    /** Le délai d'écoute est passé sans rien entendre : le dépannage du micro peut commencer. */
    micGraceOver: boolean
    /** L'étape qui vient de se régler garde la main le temps qu'on la voie. */
    heldId: DeviceCheckStepId | null
    ladders: Record<DeviceCheckStepId, LadderState>
    /** Le dernier barreau montré par étape, pour dire lequel a réglé le problème ou a été passé. */
    lastRung: Partial<Record<DeviceCheckStepId, string>>
    audioDeviceId: string | null
    videoDeviceId: string | null
}

export type DeviceCheckEvent =
    | { type: "causesProbed"; causes: Partial<Record<"camera" | "microphone", DeviceCause>> }
    | { type: "checkingChanged"; checking: boolean }
    | { type: "networkMeasured"; latencyMs: number }
    | { type: "devicesListed"; audioinput: MediaDeviceOption[]; videoinput: MediaDeviceOption[] }
    | { type: "memoryRead"; matches: boolean; reason: string | null; changedParts: string[] }
    | { type: "videoMutedChanged"; muted: boolean }
    | { type: "imageAnalysed"; obstructed: boolean; image: ImageReading }
    | { type: "previewDeviceError"; cause: DeviceCause | null }
    | { type: "started" }
    | { type: "recheckRequested" }
    | { type: "stepSkipped"; step: DeviceCheckStepId }
    | { type: "stepReopened"; step: DeviceCheckStepId }
    | { type: "speakerAnswered"; heard: boolean }
    | { type: "seesSelfAnswered"; sees: boolean }
    | { type: "nextAttempt" }
    | { type: "micNoSignal" }
    | { type: "micHeard" }
    | { type: "micGraceStarted" }
    | { type: "micGraceElapsed" }
    | { type: "holdElapsed"; step: DeviceCheckStepId }
    | { type: "chimeStarted"; run: number }
    | { type: "chimeFinished"; run: number; played: boolean }
    | { type: "deviceSelected"; kind: MediaDeviceKind; deviceId: string }

type SignalValue = string | number | boolean | null

export type DeviceCheckSignal =
    /** Un événement Amplitude du précall (`PRECALL_EVENT`), sans le contexte commun. */
    | { kind: "track"; event: string; properties: Record<string, SignalValue> }
    /** Un fil d'Ariane de la machine. */
    | { kind: "transition"; from: string; to: string; event: string; data?: Record<string, SignalValue> }
    /** Un accès vient d'être refusé : une trace à part, qu'on puisse compter. */
    | { kind: "permissionDenied"; device: "microphone" | "camera"; cause: DeviceCause }

export type DeviceCheckStepView = {
    id: DeviceCheckStepId
    label: string
    /** La mesure du jour — celle que le dépannage et le dossier lisent. */
    verdict: DeviceStatus
    /** Ce qu'on sait de l'étape : mesuré aujourd'hui ou retenu du dernier test. Ce que l'écran montre. */
    acquiredVerdict: DeviceStatus
    detail: string
    /** Passée sans être réglée : elle se lit en échec et se reprend. */
    skipped: boolean
}

export type DeviceCheckCurrentView = {
    id: DeviceCheckStepId
    /** L'étape vient de se régler : le constat, et plus aucun geste. */
    resolved: boolean
    resolvedText: string
    /** On attend que l'élève dise « allô ». */
    listening: boolean
    guidance: DeviceGuidanceLine[]
    /** La fenêtre d'autorisation du navigateur, à dessiner sous ce geste du conseil. */
    permissionHint: { device: "microphone" | "camera"; afterIndex: number } | null
    /** Les gestes proposés (`ACTION` de `precallActions`), dans leur ordre d'affichage. */
    actions: string[]
    /** Le choix de l'appareil, quand le barreau du moment le demande. */
    picker: { kind: MediaDeviceKind; options: MediaDeviceOption[]; selectedId: string | null } | null
    /** L'étape des haut-parleurs : où en est la mélodie. */
    sound: "idle" | "playing" | "played" | "blocked" | null
    /** Qui a bloqué la mélodie : le navigateur par son nom, sinon « Ton appareil ». */
    soundBlockedBy: string
    /** L'aperçu de la caméra est dans l'étape. */
    showsPreview: boolean
    /** Les barres du micro sont dans l'étape (dès que l'accès est donné, et pendant le constat). */
    showsMicLevel: boolean
    /** L'élève a déjà dit qu'il ne se voit pas : le bouton dit « Toujours pas ». */
    seesSelfDenied: boolean
}

/** Le bilan que l'entrée lit : qui prévenir, et ce que l'élève a déjà essayé. */
export type DeviceCheckReport = {
    /** Les verdicts ACQUIS (le souvenir compte pour vert). */
    microphone: DeviceStatus
    speaker: DeviceStatus
    camera: DeviceStatus
    network: DeviceStatus
    /** « Micro : autorisation »… — dix au plus, ce que l'API accepte. */
    attempts: string[]
    /** Le parcours est fini (ou n'avait rien à faire) : l'entrée devient un bouton. */
    allDone: boolean
    started: boolean
    /** La mesure brute et sa cause, appareil par appareil, pour le technicien. */
    diagnostics: {
        microphone: { status: DeviceStatus; cause: DeviceCause }
        speaker: { status: DeviceStatus }
        camera: { status: DeviceStatus; cause: DeviceCause }
        network: { status: DeviceStatus; latency_ms: number | null }
    }
}

export type DeviceCheckPhase = "probing" | "intro" | "remembered" | "journey"

export type DeviceCheckView = {
    phase: DeviceCheckPhase
    /** Pourquoi on refait le test (le matériel a changé, le dernier test date d'un mois). */
    memoryMiss: string
    /** Déjà vérifié, mais la connexion se mesure encore. */
    rememberedPending: boolean
    steps: DeviceCheckStepView[]
    /** L'étape ouverte ; `null` quand le parcours est fini (le bilan). */
    currentIndex: number | null
    current: DeviceCheckCurrentView | null
    busy: boolean
    permissions: { microphone: DevicePermission; camera: DevicePermission }
    report: DeviceCheckReport

    // Ce que le crochet lit pour poser ses effets
    /** L'appareil en défaut de l'étape ouverte, à re-sonder toutes les 5 s et au retour dans l'onglet. */
    deviceToRecheck: "microphone" | "camera" | null
    /** La connexion se remesure toutes les 9 s, pendant son étape seulement. */
    networkPolling: boolean
    /** Le délai d'écoute du micro court. */
    micGraceRunning: boolean
    /** L'aperçu est monté (étape caméra, ou carte « déjà vérifié »). */
    previewMounted: boolean
    /** L'analyse de l'image tourne (web). */
    analysingImage: boolean
    /** La caméra choisie ne s'ouvre pas : la phrase à poser sur l'aperçu. */
    previewError: string | null
    /** Quelque chose cache l'objectif : la pastille sur l'aperçu. */
    obstructed: boolean
    /** Une image est déjà là : « Couper ma vidéo » a un sens (jamais sur un cadre vide). */
    previewShowsImage: boolean
    /** Les trois appareils sont mesurés au vert : le souvenir peut s'écrire. */
    shouldRemember: boolean
    /** Le souvenir vaut, mais la connexion du jour laisse une étape : ouvrir les accès une fois. */
    memoryLeavesWork: boolean
    /** Le barreau de dépannage qui conseille l'étape ouverte, s'il y en a un. */
    rungId: string | null
    /** Où en est la machine, en un mot : ce que disent ses fils d'Ariane. */
    position: string
}

// Les règles sont en JS, et leur JSDoc ne dit pas tout ce qu'elles lisent (`cameraReady`, `image`) :
// leur signature réelle, ici.
const cameraVerdict = cameraVerdictRule as unknown as (input: {
    isVideoMuted: boolean
    opened: string
    isObstructed: boolean
    image: string
    seesSelf: boolean | null
    isWeb: boolean
}) => string
const visibleActions = visibleActionsRule as unknown as (input: Record<string, unknown>) => string[]

// ── Les constantes ───────────────────────────────────────────────────────────────────────────────

export const DEVICE_CHECK_STEPS: DeviceCheckStepId[] = ["network", "microphone", "speaker", "camera"]

/** Le nom de chaque appareil, sur la liste et dans « Déjà essayé » : une seule source. */
export const DEVICE_LABEL: Record<DeviceCheckStepId, string> = {
    network: "Connexion",
    microphone: "Micro",
    speaker: "Haut-parleurs",
    camera: "Caméra",
}

/** Ce que le souvenir couvre : ce que seul l'élève peut juger. La connexion se remesure toujours. */
const REMEMBERED_STEPS: DeviceCheckStepId[] = ["microphone", "speaker", "camera"]

const RESOLVED_TEXT: Record<DeviceCheckStepId, string> = {
    network: "Ta connexion est bonne.",
    microphone: "On t'entend, c'est parfait.",
    speaker: "Tu entends bien, c'est parfait.",
    camera: "On te voit, c'est parfait.",
}

/**
 * Le temps qu'on laisse à une réussite pour se faire voir. Le micro en demande davantage : sa preuve
 * est une mesure en mouvement, et le seuil bas peut le valider au premier bruit de la pièce.
 */
export const RESOLVED_HOLD_MS: Record<DeviceCheckStepId, number> = {
    network: 1300,
    microphone: 2400,
    speaker: 1300,
    camera: 1300,
}

/** Le temps qu'on laisse à l'élève pour dire « allô » avant de soupçonner son matériel. */
export const MIC_GRACE_MS = 12000

/** Durée de la mélodie de test, un peu arrondie vers le haut. */
export const CHIME_MS = 700

const LADDERS: Record<DeviceCheckStepId, Rung[]> = {
    network: NETWORK_LADDER as Rung[],
    microphone: MICROPHONE_LADDER as Rung[],
    speaker: SPEAKER_LADDER as Rung[],
    camera: CAMERA_LADDER as Rung[],
}

const emptyLadders = (): Record<DeviceCheckStepId, LadderState> => ({
    network: { index: 0, attempts: [] },
    microphone: { index: 0, attempts: [] },
    speaker: { index: 0, attempts: [] },
    camera: { index: 0, attempts: [] },
})

export function initialDeviceCheckState(env: DeviceCheckEnv, isVideoMuted = false): DeviceCheckState {
    return {
        env,
        causes: { camera: CAUSE.NOT_ASKED as DeviceCause, microphone: CAUSE.NOT_ASKED as DeviceCause },
        checking: false,
        latencyMs: null,
        devices: { audioinput: [], videoinput: [] },
        memory: { ready: false, matches: false, reason: null, changedParts: [] },
        isVideoMuted,
        isObstructed: false,
        imageSeen: IMAGE.LOOKING as ImageReading,
        cameraDeviceError: null,
        previewEverOpened: false,
        started: false,
        forceFull: false,
        skipped: {},
        speakerHeard: null,
        soundPlayed: false,
        chiming: false,
        soundBlocked: false,
        chimeRun: 0,
        seesSelf: null,
        micHeard: false,
        micGraceOver: false,
        heldId: null,
        ladders: emptyLadders(),
        lastRung: {},
        audioDeviceId: null,
        videoDeviceId: null,
    }
}

// ── La vue ───────────────────────────────────────────────────────────────────────────────────────

type StepFacts = {
    microphoneVerdict: DeviceStatus
    reportedMicrophoneVerdict: DeviceStatus
    micMeasured: boolean
    speakerVerdict: DeviceStatus
    networkVerdict: DeviceStatus
    cameraOpened: DeviceStatus
    cameraVerdict: DeviceStatus
    cameraStepReached: boolean
    analysingImage: boolean
    memoryApplies: boolean
}

function factsOf(state: DeviceCheckState): StepFacts {
    const { causes, env, skipped } = state
    const memoryApplies = state.memory.matches && !state.forceFull
    const microphoneVerdict = microphoneStatus(causes.microphone, state.micHeard) as DeviceStatus
    // Le micro n'est MESURÉ que quand on l'a écouté : un micro accordé jamais écouté vaut « pas encore
    // testé » vers l'entrée, sinon « Entrer sans finir » prévenait l'équipe pour rien.
    const micMeasured = causes.microphone !== CAUSE.GRANTED || state.micHeard || state.micGraceOver
    const speakerVerdict: DeviceStatus =
        state.speakerHeard === true ? "ok" : state.speakerHeard === false ? "warn" : "pending"
    const networkVerdict = networkStatus(state.latencyMs) as DeviceStatus
    const cameraOpened = cameraStatus(causes.camera) as DeviceStatus
    // Le tour de la caméra : les trois étapes d'avant ne dépendent pas d'elle.
    const cameraStepReached =
        (networkVerdict === STATUS.OK || skipped.network === true) &&
        (microphoneVerdict === STATUS.OK || skipped.microphone === true) &&
        (speakerVerdict === STATUS.OK || skipped.speaker === true)
    // L'analyse lit l'aperçu : à l'étape caméra, et sur la carte « déjà vérifié » (un miroir n'est pas
    // un examen, mais une image noire sous « tout est vérifié » se dirait avant nous).
    const analysingImage =
        env.isWeb && !state.isVideoMuted && (memoryApplies || (cameraOpened === STATUS.OK && cameraStepReached))
    const camera = cameraVerdict({
        isVideoMuted: state.isVideoMuted,
        opened: cameraOpened,
        isObstructed: state.isObstructed,
        image: state.imageSeen,
        seesSelf: state.seesSelf,
        isWeb: env.isWeb,
    }) as DeviceStatus
    return {
        microphoneVerdict,
        reportedMicrophoneVerdict: micMeasured ? microphoneVerdict : "pending",
        micMeasured,
        speakerVerdict,
        networkVerdict,
        cameraOpened,
        cameraVerdict: camera,
        cameraStepReached,
        analysingImage,
        memoryApplies,
    }
}

type LadderView = { cause: string; rungs: Rung[]; current: Rung | null; position: number; total: number }

/** La cause qui choisit les barreaux de chaque échelle, et ce qu'une étape réglée remet à zéro. */
function ladderCause(id: DeviceCheckStepId, state: DeviceCheckState, facts: StepFacts): string {
    if (id === "microphone") return state.causes.microphone
    if (id === "camera") return state.isObstructed ? CAUSE.DARK_IMAGE : state.causes.camera
    if (id === "speaker") return state.speakerHeard === false ? "unheard" : "untested"
    return facts.networkVerdict
}

function ladderResolved(id: DeviceCheckStepId, facts: StepFacts): boolean {
    if (id === "microphone") return facts.microphoneVerdict === STATUS.OK
    if (id === "camera") return facts.cameraVerdict === STATUS.OK
    if (id === "speaker") return facts.speakerVerdict === STATUS.OK
    return facts.networkVerdict === STATUS.OK
}

function ladderOf(id: DeviceCheckStepId, state: DeviceCheckState, facts: StepFacts): LadderView {
    const cause = ladderCause(id, state, facts)
    const rungs = relevantRungs(LADDERS[id], {
        cause,
        isWeb: state.env.isWeb,
        // Le choix d'un appareil n'a de sens que s'il y en a plusieurs.
        deviceCount:
            id === "microphone"
                ? state.devices.audioinput.length
                : id === "camera"
                  ? state.devices.videoinput.length
                  : 0,
        isVideoMuted: id === "camera" ? state.isVideoMuted : undefined,
    }) as Rung[]
    const { index } = state.ladders[id]
    return {
        cause,
        rungs,
        current: index < rungs.length ? (rungs[index] ?? null) : null,
        position: Math.min(index + 1, rungs.length),
        total: rungs.length,
    }
}

function permissionOf(cause: DeviceCause): DevicePermission {
    if (cause === CAUSE.NOT_ASKED) return "prompt"
    if (cause === CAUSE.GRANTED || cause === CAUSE.DARK_IMAGE) return "granted"
    if (cause === CAUSE.DENIED || cause === CAUSE.BLOCKED_BY_SYSTEM) return "denied"
    return "unavailable"
}

/**
 * Sous la ligne « Autoriser » du chemin : ce qui va s'ouvrir. Pas sur téléphone, ni sur un chemin qui
 * n'en a pas (Firefox : on retire « Bloqué », `permissionChoiceIndex`).
 */
function permissionHintOf(
    env: DeviceCheckEnv,
    permissionRung: boolean,
    currentId: string
): { device: "microphone" | "camera"; afterIndex: number } | null {
    if (!env.isWeb || !permissionRung) return null
    const afterIndex = permissionChoiceIndex(env.permissionBrowser)
    return afterIndex != null ? { device: currentId === "camera" ? "camera" : "microphone", afterIndex } : null
}

const toLines = (tip: string | { icon?: string; text: string }[]): DeviceGuidanceLine[] =>
    typeof tip === "string" ? [{ text: tip }] : tip.map((line) => ({ text: line.text, icon: line.icon }))

function detailOf(id: DeviceCheckStepId, state: DeviceCheckState, facts: StepFacts): string {
    const { env, causes } = state
    if (id === "network") return networkDetail(state.latencyMs)
    // Ce que le souvenir couvre se lit comme tel : ni « pas encore testé », ni « on t'entend ».
    if (facts.memoryApplies && REMEMBERED_STEPS.includes(id)) return "Vérifié la dernière fois"
    // Un micro accordé qu'on n'a pas encore écouté n'est pas « muet ».
    if (id === "microphone" && !facts.micMeasured) return "Pas encore testé"
    if (id === "microphone") return microphoneDetail(causes.microphone, env.isWeb, state.micHeard)
    if (id === "speaker") {
        if (state.speakerHeard === true) return "Tu entends bien"
        if (state.speakerHeard === false) return "Tu n'entends rien"
        return "Pas encore testé"
    }
    if (state.isVideoMuted) return "Ta vidéo est coupée"
    if (state.isObstructed) return "Quelque chose cache la caméra"
    if (facts.analysingImage && state.imageSeen === IMAGE.LOOKING) return "Je regarde ton image…"
    const cameraSentence = cameraDetail(causes.camera, env.isWeb)
    if (facts.cameraVerdict === STATUS.OK) return cameraSentence
    if (!facts.cameraStepReached && facts.cameraOpened === STATUS.OK) return "Pas encore vérifiée"
    if (facts.cameraOpened === STATUS.OK && state.seesSelf === null) return "Regarde ton image"
    if (state.seesSelf === false) return "Tu ne te vois pas bien"
    return cameraSentence
}

function verdictOf(id: DeviceCheckStepId, facts: StepFacts): DeviceStatus {
    if (id === "network") return facts.networkVerdict
    if (id === "microphone") return facts.microphoneVerdict
    if (id === "speaker") return facts.speakerVerdict
    return facts.cameraVerdict
}

/** Le conseil de l'étape ouverte : un seul geste, l'autorisation d'abord. */
function guidanceOf(
    current: DeviceCheckStepId,
    state: DeviceCheckState,
    facts: StepFacts,
    ladder: LadderView,
    rung: Rung | null,
    needsAuthorization: boolean
): DeviceGuidanceLine[] {
    const { env } = state
    const copy = audienceCopy(env.audience)
    const tip = (r: Rung) =>
        toLines(
            r.tip({
                isWeb: env.isWeb,
                cause: ladder.cause,
                browser: env.permissionBrowser,
                audience: env.audience,
            })
        )
    if (needsAuthorization && (current === "camera" || current === "microphone")) return toLines(copy.needConsent)
    // Tant que rien ne cloche, le conseil de la caméra est une invitation à se regarder.
    if (
        current === "camera" &&
        !state.isObstructed &&
        facts.cameraOpened === STATUS.OK &&
        !state.isVideoMuted &&
        state.seesSelf === null
    ) {
        return toLines(
            env.isWeb && state.imageSeen === IMAGE.LOOKING
                ? "Deux secondes, je vérifie que ton image n'est pas noire."
                : "Regarde l'image : est-ce qu'on voit bien ton visage ?"
        )
    }
    if (current === "camera" && state.isObstructed) {
        return toLines("Enlève ce qui cache la caméra : le petit cache, un autocollant, ou ton doigt.")
    }
    if (current === "network" && facts.networkVerdict === STATUS.PENDING) {
        return toLines("Je mesure ta connexion, deux secondes.")
    }
    if (current === "speaker") {
        if (state.speakerHeard !== false) {
            return toLines("Clique sur le bouton et écoute bien : tu dois entendre une petite musique.")
        }
        if (rung) return tip(rung)
        return toLines("Tu n'entends toujours rien ? Entre quand même, et écris-le tout de suite dans le chat.")
    }
    if (rung) return tip(rung)
    // L'échelle est épuisée : une fin, et elle dit la vérité.
    if (ladder.total > 0 && ladder.position >= ladder.total) {
        if (current === "camera") return toLines(copy.cameraGiveUp)
        if (current === "microphone") {
            return toLines(
                "Ça ne marche toujours pas ? Entre quand même : tu pourras écrire dans le chat, et redire « allô » pendant le live."
            )
        }
        return toLines("On ne peut pas réparer le Wi-Fi d'ici. Entre quand même, et coupe ta vidéo si ça saccade.")
    }
    if (current === "camera") return toLines("Regarde l'image : est-ce que tu te vois bien ?")
    return toLines("Dis « allô » — les barres doivent bouger.")
}

/**
 * L'enregistreur du micro tourne-t-il ? À l'étape du micro, l'accès donné (`showsMicLevel`) — sauf quand
 * l'écran rend les appareils avant d'entrer dans le live (`released`) : au natif, la salle publiera son micro.
 */
export function microphoneListening(view: Pick<DeviceCheckView, "current">, released: boolean): boolean {
    return view.current?.showsMicLevel === true && !released
}

export function deriveDeviceCheck(state: DeviceCheckState): DeviceCheckView & { openId: DeviceCheckStepId | null } {
    const facts = factsOf(state)
    const { env, causes, skipped } = state
    const isRemembered = (id: DeviceCheckStepId) => facts.memoryApplies && REMEMBERED_STEPS.includes(id)
    const acquired = (id: DeviceCheckStepId, verdict: DeviceStatus): DeviceStatus => (isRemembered(id) ? "ok" : verdict)

    const steps: DeviceCheckStepView[] = DEVICE_CHECK_STEPS.map((id) => {
        const verdict = verdictOf(id, facts)
        const acquiredVerdict = acquired(id, verdict)
        return {
            id,
            label: DEVICE_LABEL[id],
            verdict,
            acquiredVerdict,
            detail: detailOf(id, state, facts),
            skipped: skipped[id] === true && acquiredVerdict !== STATUS.OK,
        }
    })

    // Derrière soi : mesurée bonne aujourd'hui, couverte par le souvenir, ou passée.
    const isValidated = (step: DeviceCheckStepView) => step.acquiredVerdict === STATUS.OK || skipped[step.id] === true
    const openIndex = steps.findIndex((step) => !isValidated(step))
    const openId = openIndex === -1 ? null : steps[openIndex]!.id

    const memoryVerdictsPending = facts.memoryApplies && facts.networkVerdict === STATUS.PENDING
    const memoryLeavesWork = facts.memoryApplies && !memoryVerdictsPending && openIndex !== -1
    const journeyVisible = state.started || memoryLeavesWork
    const probing = !state.started && !state.memory.ready
    const phase: DeviceCheckPhase = journeyVisible
        ? "journey"
        : probing
          ? "probing"
          : facts.memoryApplies
            ? "remembered"
            : "intro"

    const heldIndex = state.heldId != null ? steps.findIndex((step) => step.id === state.heldId) : -1
    const currentIndex = heldIndex !== -1 ? heldIndex : openIndex
    const currentId = currentIndex === -1 ? null : steps[currentIndex]!.id
    const micGranted = causes.microphone === CAUSE.GRANTED
    const needsAuthorization = causes.camera === CAUSE.NOT_ASKED || causes.microphone === CAUSE.NOT_ASKED

    let current: DeviceCheckCurrentView | null = null
    let rungId: string | null = null
    if (currentId != null) {
        const ladder = ladderOf(currentId, state, facts)
        // Le dépannage ne commence qu'une fois qu'il y a quelque chose à réparer, et qu'on sait quoi.
        const notYetDiagnosed =
            (currentId === "speaker" && state.speakerHeard !== false) ||
            (currentId === "network" && facts.networkVerdict === STATUS.PENDING) ||
            (currentId === "microphone" && micGranted && !state.micHeard && !state.micGraceOver) ||
            (currentId === "camera" &&
                !state.isObstructed &&
                facts.cameraOpened === STATUS.OK &&
                !state.isVideoMuted &&
                state.seesSelf === null)
        const rung = notYetDiagnosed ? null : ladder.current
        rungId = rung?.id ?? null
        const listening = currentId === "microphone" && micGranted && !state.micHeard && !state.micGraceOver
        const actions = visibleActions({
            stepId: currentId,
            needsAuthorization,
            isWeb: env.isWeb,
            isVideoMuted: state.isVideoMuted,
            isObstructed: state.isObstructed,
            image: state.imageSeen,
            soundPlayed: state.soundPlayed,
            speakerHeard: state.speakerHeard,
            seesSelf: state.seesSelf,
            rungId,
            // Le dernier barreau a droit à son « Ça n'a pas marché » (`<=`, pas `<`).
            hasMoreAttempts: ladder.position <= ladder.total,
            micListening: listening,
            cameraReady: facts.cameraOpened === STATUS.OK,
        }) as string[]
        const picker =
            rung?.picker != null
                ? {
                      kind: rung.picker,
                      options: state.devices[rung.picker],
                      selectedId: rung.picker === "audioinput" ? state.audioDeviceId : state.videoDeviceId,
                  }
                : null
        current = {
            id: currentId,
            resolved: state.heldId === currentId,
            resolvedText: RESOLVED_TEXT[currentId],
            listening,
            guidance: guidanceOf(currentId, state, facts, ladder, rung, needsAuthorization),
            // Sous la ligne « Choisis Autoriser » : ce qui va s'ouvrir. Pas sur téléphone.
            permissionHint: permissionHintOf(env, rung?.id === "permission", currentId),
            actions,
            picker,
            sound:
                currentId === "speaker"
                    ? state.soundBlocked
                        ? "blocked"
                        : state.chiming
                          ? "playing"
                          : state.soundPlayed
                            ? "played"
                            : "idle"
                    : null,
            soundBlockedBy: env.isWeb ? browserLabel(env.browser) : "Ton appareil",
            showsPreview: phase === "journey" && currentId === "camera",
            // Les barres n'écoutent que dans le parcours : sur l'accueil, le micro reste fermé.
            showsMicLevel: phase === "journey" && currentId === "microphone" && micGranted,
            seesSelfDenied: state.seesSelf === false,
        }
    }

    const attempts = (["microphone", "speaker", "camera", "network"] as DeviceCheckStepId[])
        .flatMap((id) => state.ladders[id].attempts.map((label) => `${DEVICE_LABEL[id]} : ${label}`))
        .slice(0, 10)

    const allMeasuredOk = REMEMBERED_STEPS.every((id) => verdictOf(id, facts) === STATUS.OK)
    const previewMounted = phase === "remembered" || (phase === "journey" && currentId === "camera")
    // L'aperçu web est le seul à savoir si une image est déjà venue : en natif il n'échoue jamais.
    const previewShowsImage = !env.isWeb || state.previewEverOpened

    return {
        phase,
        memoryMiss: memoryMissDetail(state.memory.reason),
        rememberedPending: memoryVerdictsPending,
        steps,
        currentIndex: currentIndex === -1 ? null : currentIndex,
        current,
        busy: state.checking,
        permissions: { microphone: permissionOf(causes.microphone), camera: permissionOf(causes.camera) },
        report: {
            microphone: acquired("microphone", facts.reportedMicrophoneVerdict),
            speaker: acquired("speaker", facts.speakerVerdict),
            camera: acquired("camera", facts.cameraVerdict),
            network: acquired("network", facts.networkVerdict),
            attempts,
            // Pas tant qu'un verdict du souvenir est en vol : le bouton paraîtrait pour se rétracter.
            allDone: currentIndex === -1 && !memoryVerdictsPending,
            started: state.started,
            diagnostics: {
                microphone: { status: facts.microphoneVerdict, cause: causes.microphone },
                speaker: { status: facts.speakerVerdict },
                camera: { status: facts.cameraVerdict, cause: causes.camera },
                network: { status: facts.networkVerdict, latency_ms: state.latencyMs },
            },
        },
        deviceToRecheck:
            (currentId === "microphone" || currentId === "camera") && causes[currentId] !== CAUSE.GRANTED
                ? currentId
                : null,
        networkPolling: currentId === "network" && facts.networkVerdict !== STATUS.PENDING,
        micGraceRunning: state.started && openId === "microphone" && micGranted && !state.micHeard,
        previewMounted,
        analysingImage: facts.analysingImage,
        previewError:
            state.cameraDeviceError != null && !state.isVideoMuted
                ? previewShowsImage
                    ? `${cameraDetail(state.cameraDeviceError, env.isWeb)} — tu vois toujours la précédente.`
                    : cameraDetail(state.cameraDeviceError, env.isWeb)
                : null,
        obstructed: state.isObstructed,
        previewShowsImage,
        shouldRemember: state.started && allMeasuredOk,
        memoryLeavesWork,
        rungId,
        position:
            phase === "journey"
                ? `journey:${currentId ?? "done"}${state.heldId != null ? ":held" : ""}${rungId != null ? `:${rungId}` : ""}`
                : phase,
        openId,
    }
}

// ── Le réducteur ─────────────────────────────────────────────────────────────────────────────────

/** Un essai de plus sur une échelle : le barreau tenté entre dans « Déjà essayé ». */
function advance(state: DeviceCheckState, id: DeviceCheckStepId, signals: DeviceCheckSignal[]): DeviceCheckState {
    const facts = factsOf(state)
    const ladder = ladderOf(id, state, facts)
    const { index, attempts } = state.ladders[id]
    signals.push({
        kind: "track",
        event: PRECALL_EVENT.RUNG_RESULT,
        properties: { step: id, rung: ladder.current?.id ?? null, result: "next" },
    })
    const tried = index < ladder.rungs.length ? ladder.rungs[index]!.label : null
    return {
        ...state,
        ladders: {
            ...state.ladders,
            [id]: {
                index: index + 1,
                attempts: tried != null && !attempts.includes(tried) ? [...attempts, tried] : attempts,
            },
        },
    }
}

function apply(state: DeviceCheckState, event: DeviceCheckEvent, signals: DeviceCheckSignal[]): DeviceCheckState {
    switch (event.type) {
        case "causesProbed": {
            const causes = { ...state.causes, ...event.causes }
            if (causes.camera === state.causes.camera && causes.microphone === state.causes.microphone) return state
            // Un casque rebranché mérite qu'on réécoute avant de le suspecter à nouveau.
            const micChanged = causes.microphone !== state.causes.microphone
            return { ...state, causes, micHeard: micChanged ? false : state.micHeard }
        }
        case "checkingChanged":
            return state.checking === event.checking ? state : { ...state, checking: event.checking }
        case "networkMeasured":
            return { ...state, latencyMs: event.latencyMs }
        case "devicesListed":
            return { ...state, devices: { audioinput: event.audioinput, videoinput: event.videoinput } }
        case "memoryRead":
            return {
                ...state,
                memory: { ready: true, matches: event.matches, reason: event.reason, changedParts: event.changedParts },
            }
        case "videoMutedChanged":
            return state.isVideoMuted === event.muted ? state : { ...state, isVideoMuted: event.muted }
        case "imageAnalysed": {
            // Un verdict l'emporte sur un renoncement, et une image jugée ne redevient pas une question.
            const imageSeen =
                event.image === IMAGE.LOOKING
                    ? state.imageSeen
                    : state.imageSeen === IMAGE.JUDGED
                      ? state.imageSeen
                      : event.image
            return { ...state, isObstructed: event.obstructed, imageSeen }
        }
        case "previewDeviceError":
            return {
                ...state,
                cameraDeviceError: event.cause,
                previewEverOpened: event.cause == null ? true : state.previewEverOpened,
            }
        case "started":
            return state.started ? state : { ...state, started: true }
        case "recheckRequested":
            return { ...state, forceFull: true }
        case "stepSkipped":
            signals.push({
                kind: "track",
                event: PRECALL_EVENT.RUNG_RESULT,
                properties: { step: event.step, rung: state.lastRung[event.step] ?? null, result: "skipped" },
            })
            return { ...state, skipped: { ...state.skipped, [event.step]: true } }
        case "stepReopened": {
            // Passer n'est pas définitif : le casque enfin branché, l'étape se reprend.
            const skipped = { ...state.skipped }
            delete skipped[event.step]
            return { ...state, skipped }
        }
        case "speakerAnswered":
            if (event.heard) return { ...state, speakerHeard: true }
            // « Non » est l'essai suivant, une fois la première réponse donnée.
            return state.speakerHeard === false ? advance(state, "speaker", signals) : { ...state, speakerHeard: false }
        case "seesSelfAnswered":
            if (event.sees) return { ...state, seesSelf: true }
            return state.seesSelf === false ? advance(state, "camera", signals) : { ...state, seesSelf: false }
        case "nextAttempt": {
            const currentId = deriveDeviceCheck(state).current?.id
            return currentId != null ? advance(state, currentId, signals) : state
        }
        case "micNoSignal":
        case "micGraceElapsed":
            return { ...state, micGraceOver: true }
        case "micGraceStarted":
            return { ...state, micGraceOver: false }
        case "micHeard":
            return state.micHeard ? state : { ...state, micHeard: true }
        case "holdElapsed":
            return state.heldId === event.step ? { ...state, heldId: null } : state
        case "chimeStarted":
            return { ...state, chimeRun: event.run, soundPlayed: true, soundBlocked: false, chiming: true }
        case "chimeFinished":
            if (event.run !== state.chimeRun) return state
            return { ...state, chiming: false, soundBlocked: !event.played }
        case "deviceSelected":
            return event.kind === "audioinput"
                ? { ...state, audioDeviceId: event.deviceId }
                : { ...state, videoDeviceId: event.deviceId }
        default:
            return state
    }
}

/** Les échelles suivent leur cause : une cause qui change repart du premier barreau, une étape réglée efface tout. */
function settleLadders(before: DeviceCheckState, after: DeviceCheckState): DeviceCheckState {
    const factsBefore = factsOf(before)
    const factsAfter = factsOf(after)
    let ladders = after.ladders
    for (const id of DEVICE_CHECK_STEPS) {
        const resolvedNow = ladderResolved(id, factsAfter)
        if (resolvedNow && !ladderResolved(id, factsBefore)) {
            ladders = { ...ladders, [id]: { index: 0, attempts: [] } }
        } else if (
            ladderCause(id, before, factsBefore) !== ladderCause(id, after, factsAfter) &&
            ladders[id].index !== 0
        ) {
            ladders = { ...ladders, [id]: { ...ladders[id], index: 0 } }
        }
    }
    return ladders === after.ladders ? after : { ...after, ladders }
}

const MEASURED_VERDICT: Record<DeviceCheckStepId, (state: DeviceCheckState) => DeviceStatus> = {
    network: (state) => factsOf(state).networkVerdict,
    microphone: (state) => factsOf(state).reportedMicrophoneVerdict,
    speaker: (state) => factsOf(state).speakerVerdict,
    camera: (state) => factsOf(state).cameraVerdict,
}

/**
 * Une transition : l'état d'après, et ce qu'elle laisse derrière elle (Amplitude, fils d'Ariane,
 * refus d'un accès). Pur : rejouer une suite d'événements rend toujours la même suite de signaux.
 */
export function deviceCheckReducer(
    state: DeviceCheckState,
    event: DeviceCheckEvent
): { state: DeviceCheckState; signals: DeviceCheckSignal[] } {
    const signals: DeviceCheckSignal[] = []
    const before = deriveDeviceCheck(state)
    let next = settleLadders(state, apply(state, event, signals))
    if (next === state) return { state, signals }
    let after = deriveDeviceCheck(next)

    // L'étape qui vient de se régler garde la main : la bonne nouvelle se voit avant la suite.
    // Rien à célébrer hors du parcours, ni sur une étape passée.
    const settled = before.openId
    if (settled != null && settled !== after.openId && next.started) {
        const verdict = after.steps.find((step) => step.id === settled)?.verdict
        if (verdict === STATUS.OK && next.skipped[settled] !== true) {
            const rung = next.lastRung[settled]
            if (rung != null) {
                signals.push({
                    kind: "track",
                    event: PRECALL_EVENT.RUNG_RESULT,
                    properties: { step: settled, rung, result: "resolved" },
                })
            }
            next = { ...next, heldId: settled }
            after = deriveDeviceCheck(next)
        }
    }

    // L'aperçu repart de zéro à chaque ouverture de l'étape caméra.
    if (after.current?.id === "camera" && before.current?.id !== "camera" && next.previewEverOpened) {
        next = { ...next, previewEverOpened: false }
        after = deriveDeviceCheck(next)
    }

    // Un barreau s'affiche : on le note, pour dire plus tard s'il a réglé le problème.
    const rungStep = after.current?.id
    if (
        rungStep != null &&
        after.rungId != null &&
        (after.rungId !== before.rungId || before.current?.id !== rungStep)
    ) {
        next = { ...next, lastRung: { ...next.lastRung, [rungStep]: after.rungId } }
        signals.push({
            kind: "track",
            event: PRECALL_EVENT.RUNG_SHOWN,
            properties: { step: rungStep, rung: after.rungId },
        })
    }

    // Un verdict mesuré par appareil, à chaque changement — jamais « en attente ».
    for (const id of DEVICE_CHECK_STEPS) {
        const was = MEASURED_VERDICT[id](state)
        const now = MEASURED_VERDICT[id](next)
        if (now === was || now === STATUS.PENDING) continue
        const properties: Record<string, SignalValue> = { step: id, verdict: now }
        if (id === "network") properties.latency_ms = next.latencyMs
        if (id === "microphone" || id === "camera") properties.cause = next.causes[id]
        signals.push({ kind: "track", event: PRECALL_EVENT.STEP_VERDICT, properties })
    }

    // L'écran s'affiche : par le chemin court, ou par le parcours.
    if (event.type === "memoryRead" && !state.memory.ready) {
        const properties: Record<string, SignalValue> = { memory_hit: event.matches, memory_miss: event.reason }
        if (event.changedParts.length > 0) properties.memory_miss_detail = event.changedParts.join(",")
        signals.push({ kind: "track", event: PRECALL_EVENT.OPENED, properties })
    }

    // Les accès, dits en clair : chaque changement laisse un fil d'Ariane, un refus une trace.
    for (const device of ["microphone", "camera"] as const) {
        const was = before.permissions[device]
        const now = after.permissions[device]
        if (was === now) continue
        signals.push({
            kind: "transition",
            from: `${device}:${was}`,
            to: `${device}:${now}`,
            event: event.type,
            data: { cause: next.causes[device] },
        })
        if (now === "denied") signals.push({ kind: "permissionDenied", device, cause: next.causes[device] })
    }

    if (before.position !== after.position) {
        signals.push({ kind: "transition", from: before.position, to: after.position, event: event.type })
    }

    return { state: next, signals }
}
